import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchHierarchyTree,
  type HierarchyTreeNode,
} from '@/api/productHierarchy'
import { queryKeys } from '@/lib/queryKeys'
import type { DropdownOptionRead } from '@/types/dag'

const STALE_TIME = 10 * 60 * 1000

function nodeToDisplayValue(node: HierarchyTreeNode): string {
  return node.name
}

function nodesToDropdownOptions(
  nodes: HierarchyTreeNode[]
): DropdownOptionRead[] {
  return nodes.map(n => ({
    value: nodeToDisplayValue(n),
    display_label: nodeToDisplayValue(n),
  }))
}

export interface HierarchyForDagData {
  divisionOptions: DropdownOptionRead[]
  getDepartmentOptions: (
    divisionDisplayValue: string | null
  ) => DropdownOptionRead[]
  getClassOptions: (
    departmentDisplayValue: string | null
  ) => DropdownOptionRead[]
  getSubclassOptions: (classDisplayValue: string | null) => DropdownOptionRead[]
}

/**
 * Hook that loads the full hierarchy tree and provides lookup functions
 * for the DAG table's cascading department → class → subclass dropdowns.
 */
export function useHierarchyForDag(): HierarchyForDagData | null {
  const { data: tree } = useQuery({
    queryKey: queryKeys.hierarchy.tree(),
    queryFn: fetchHierarchyTree,
    staleTime: STALE_TIME,
  })

  return useMemo(() => {
    if (!tree) return null

    // Build a reverse map: "code - name" → HierarchyTreeNode (with children)
    const displayMap = new Map<string, HierarchyTreeNode>()

    function indexTree(nodes: HierarchyTreeNode[]) {
      for (const node of nodes) {
        displayMap.set(nodeToDisplayValue(node), node)
        // Also index by name alone and code alone as fallback
        displayMap.set(node.name, node)
        displayMap.set(node.code, node)
        if (node.children) indexTree(node.children)
      }
    }
    indexTree(tree)

    // Collect divisions (top-level nodes)
    const divisions = tree.filter(n => n.level === 'DIVISION')
    const divisionOptions = nodesToDropdownOptions(divisions)

    function getDepartmentOptions(
      divisionDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!divisionDisplayValue) return []
      const divNode = displayMap.get(divisionDisplayValue)
      if (!divNode || divNode.level !== 'DIVISION') return []
      const depts = (divNode.children ?? []).filter(
        c => c.level === 'DEPARTMENT'
      )
      return nodesToDropdownOptions(depts)
    }

    function getClassOptions(
      departmentDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!departmentDisplayValue) return []
      const deptNode = displayMap.get(departmentDisplayValue)
      if (!deptNode || deptNode.level !== 'DEPARTMENT') return []
      const classes = (deptNode.children ?? []).filter(c => c.level === 'CLASS')
      return nodesToDropdownOptions(classes)
    }

    function getSubclassOptions(
      classDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!classDisplayValue) return []
      const classNode = displayMap.get(classDisplayValue)
      if (!classNode || classNode.level !== 'CLASS') return []
      const subclasses = (classNode.children ?? []).filter(
        c => c.level === 'SUBCLASS'
      )
      return nodesToDropdownOptions(subclasses)
    }

    return {
      divisionOptions,
      getDepartmentOptions,
      getClassOptions,
      getSubclassOptions,
    }
  }, [tree])
}
